// src/pages/Dashboard.js
import React, { useEffect, useState } from 'react';
import { getDashboard, getPendingPhysicalOrders } from '../services/api';
import { useNavigate } from 'react-router-dom';

function StatCard({ icon, color, value, label, sub }) {
  return (
    <div className="stat-card">
      <div className={`stat-icon ${color}`}>{icon}</div>
      <div className="stat-info">
        <h3>{value ?? '—'}</h3>
        <p>{label}</p>
        {sub && <p style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{sub}</p>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats,   setStats]   = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([getDashboard(), getPendingPhysicalOrders()])
      .then(([dashRes, pendRes]) => {
        setStats(dashRes.data?.data);
        setPending(pendRes.data?.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const refresh = () => {
    setLoading(true);
    Promise.all([getDashboard(), getPendingPhysicalOrders()])
      .then(([dashRes, pendRes]) => {
        setStats(dashRes.data?.data);
        setPending(pendRes.data?.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  if (loading) return (
    <div className="loading"><div className="spinner" /> Loading dashboard...</div>
  );

  const fmtRevenue = (v) => v != null ? `₹${Number(v).toFixed(0)}` : '—';

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p>Overview of CivicSetu platform</p>
        </div>
        <button className="btn btn-secondary" onClick={refresh}>🔄 Refresh</button>
      </div>

      {/* ── Row 1: Core stats ─────────────────────────────────── */}
      <div className="stats-grid">
        <StatCard icon="👥" color="blue"   value={stats?.totalUsers}            label="Total Users" />
        <StatCard icon="📦" color="green"  value={stats?.totalOrders}           label="Total Orders"
          sub={`Digital: ${stats?.digitalOrders ?? 0} · Physical: ${stats?.physicalOrders ?? 0}`} />
        <StatCard icon="💰" color="orange" value={fmtRevenue(stats?.totalRevenue)} label="Total Revenue"
          sub={`Digital: ${fmtRevenue(stats?.digitalRevenue)} · Physical: ${fmtRevenue(stats?.physicalRevenue)}`} />
        <StatCard icon="🚚" color="red"    value={stats?.pendingPhysicalOrders ?? pending.length} label="Pending Physical" />
        <StatCard icon="📱" color="purple" value={stats?.activeQrCodes}         label="Active QR Codes"
          sub={`Vehicle: ${stats?.totalVehicleQRs ?? 0} · Family: ${stats?.totalFamilyQRs ?? 0}`} />
      </div>

      {/* ── Row 2: Revenue breakdown cards ───────────────────── */}
      <div className="stats-grid" style={{ marginTop: 8 }}>
        <StatCard icon="📊" color="blue"   value={stats?.todayOrders}  label="Orders Today" />
        <StatCard icon="🔔" color="orange" value={stats?.totalAlerts}  label="Total Alerts"
          sub={`Today: ${stats?.todayAlerts ?? 0}`} />
        <div className="stat-card" style={{ background: '#f0fff4', border: '1px solid #b7e4c7' }}>
          <div className="stat-icon green">💳</div>
          <div className="stat-info">
            <h3>{fmtRevenue(stats?.digitalRevenue)}</h3>
            <p>Digital Revenue</p>
            <p style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
              {stats?.digitalOrders ?? 0} orders @ avg ₹{
                stats?.digitalOrders > 0
                  ? Math.round(stats.digitalRevenue / stats.digitalOrders)
                  : 0
              }
            </p>
          </div>
        </div>
        <div className="stat-card" style={{ background: '#f0f0ff', border: '1px solid #c4c4f0' }}>
          <div className="stat-icon purple">📬</div>
          <div className="stat-info">
            <h3>{fmtRevenue(stats?.physicalRevenue)}</h3>
            <p>Physical Revenue</p>
            <p style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
              {stats?.physicalOrders ?? 0} orders @ avg ₹{
                stats?.physicalOrders > 0
                  ? Math.round(stats.physicalRevenue / stats.physicalOrders)
                  : 0
              }
            </p>
          </div>
        </div>
      </div>

      {/* ── Pending Physical Orders table ─────────────────────── */}
      {pending.length > 0 ? (
        <div className="card" style={{ marginTop: 24 }}>
          <div className="card-header">
            <span className="card-title">
              🚨 Pending Physical Orders — Action Required ({pending.length})
            </span>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/admin/orders')}>
              View All Orders
            </button>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Order Number</th>
                  <th>Customer</th>
                  <th>Entity</th>
                  <th>Qty × Material</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Shipping Address</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pending.map(order => (
                  <tr key={order.id}>
                    <td><code style={{ fontSize: 11 }}>{order.orderNumber}</code></td>
                    <td>
                      {order.userName}<br/>
                      <span style={{ fontSize: 11, color: '#888' }}>{order.userPhone}</span>
                    </td>
                    <td><span className="badge badge-purple">{order.entityType}</span></td>
                    <td>{order.quantity}× {order.materialType || 'PAPER'}</td>
                    <td>₹{order.totalAmount}</td>
                    <td>
                      <span className={`badge ${order.orderStatus === 'PROCESSING' ? 'badge-info' : 'badge-warning'}`}>
                        {order.orderStatus}
                      </span>
                    </td>
                    <td style={{ maxWidth: 200, fontSize: 12 }}>
                      {order.shippingAddressLine1}, {order.shippingCity} — {order.shippingPincode}
                    </td>
                    <td>
                      <button className="btn btn-success btn-sm" onClick={() => navigate('/admin/orders')}>
                        Process
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card" style={{ marginTop: 24 }}>
          <div style={{ textAlign: 'center', padding: '20px', color: '#137333' }}>
            ✅ No pending physical orders — all caught up!
          </div>
        </div>
      )}
    </div>
  );
}
